import React, { useMemo } from 'react';
import {
  FaCalendarCheck,
  FaClock,
  FaCloudRain,
  FaCloudSun,
  FaCloudSunRain,
  FaExclamationTriangle,
  FaTemperatureHigh,
  FaTint,
  FaWind,
} from 'react-icons/fa';
import {
  buildForecastOperationalInsights,
  evaluateForecastRisks,
  formatForecastDate,
  forecastDayLabel,
  forecastFullDayLabel,
  isFavorableOperationalDay,
} from './forecastOperationalService';
import './ForecastOperationalOverview.css';

function normalizeText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();
}

function weatherIcon(rain) {
  if (rain >= 5) return <FaCloudRain aria-hidden="true" />;
  if (rain > 0) return <FaCloudSunRain aria-hidden="true" />;
  return <FaCloudSun aria-hidden="true" />;
}

function phraseForDay(dateValue) {
  const label = forecastFullDayLabel(dateValue);
  if (label === 'Hoje') return 'hoje';
  if (label === 'Amanhã') return 'amanhã';
  return `${label === 'sábado' || label === 'domingo' ? 'no' : 'na'} ${label}`;
}

function propertyContext(properties, municipio) {
  const normalizedMunicipio = normalizeText(municipio);
  const matches = (properties || []).filter(property => {
    const propertyMunicipio = property?.municipio || String(property?.location || '').split(',')[0];
    return normalizeText(propertyMunicipio) === normalizedMunicipio;
  });
  if (matches.length === 1) return `${matches[0].nome || matches[0].name} · ${municipio}`;
  if (matches.length > 1) return `${matches.length} propriedades · ${municipio}`;
  return `Previsão operacional · ${municipio}`;
}

export default function ForecastOperationalOverview({
  dados,
  municipio,
  onShowHourly,
  properties = [],
  modelLabel = 'GFS',
}) {
  const insights = useMemo(() => buildForecastOperationalInsights(dados), [dados]);
  const contextLabel = useMemo(() => propertyContext(properties, municipio), [properties, municipio]);

  if (!insights) return null;

  const primaryDay = insights.primaryDay;
  const firstRisk = insights.firstRisk;
  const firstRiskDescription = firstRisk
    ? `${phraseForDay(firstRisk.day.date)}: ${firstRisk.risks.map(risk => risk.label).join(', ')}.`
    : null;

  return (
    <section className="forecast-op" aria-labelledby="forecast-op-title">
      <div className="forecast-op-hero">
        <div className="forecast-op-copy">
          <span className="forecast-op-eyebrow">{contextLabel} · Modelo {modelLabel}</span>
          <h2 id="forecast-op-title">Resumo da previsão climática</h2>
          <p>
            {firstRiskDescription
              ? `A primeira condição relevante do período aparece ${firstRiskDescription} Confira a operação planejada e confirme as condições locais antes de executar.`
              : 'Não foram identificados limites críticos nos próximos sete dias. Continue acompanhando a demanda hídrica e as atualizações do modelo.'}
          </p>
          <div className="forecast-op-badges">
            <span><FaWind aria-hidden="true" /> Vento {Number(primaryDay.ws2m || 0).toFixed(1)} m/s</span>
            <span><FaTint aria-hidden="true" /> Umidade {Number(primaryDay.ur || 0).toFixed(0)}%</span>
            <span><FaCloudRain aria-hidden="true" /> Chuva {Number(primaryDay.ppt || 0).toFixed(1)} mm</span>
            <span><FaTemperatureHigh aria-hidden="true" /> Máxima {Number(primaryDay.tmax || 0).toFixed(0)}°C</span>
          </div>
        </div>
        <div className="forecast-op-actions">
          {typeof onShowHourly === 'function' && (
            <button type="button" className="is-primary" onClick={onShowHourly}>
              <FaClock aria-hidden="true" /> Ver dados horários
            </button>
          )}
        </div>
      </div>

      <div className="forecast-op-kpis" aria-label="Resumo operacional da previsão">
        <article>
          <span><FaCloudRain aria-hidden="true" /> Chuva em 3 dias</span>
          <strong>{insights.rainThree.toFixed(1)} mm</strong>
          <small>{insights.rainThree >= 10 ? 'Volume relevante previsto' : 'Baixo acumulado no período'}</small>
        </article>
        <article>
          <span><FaTint aria-hidden="true" /> Demanda em 7 dias</span>
          <strong>{insights.etoSeven.toFixed(1)} mm</strong>
          <small>Evapotranspiração acumulada</small>
        </article>
        <article>
          <span><FaTemperatureHigh aria-hidden="true" /> Maior temperatura</span>
          <strong>{insights.maxTemperature.toFixed(0)}°C</strong>
          <small>{insights.maxTemperature >= 34 ? 'Atenção ao estresse térmico' : 'Sem calor crítico previsto'}</small>
        </article>
        <article>
          <span><FaCalendarCheck aria-hidden="true" /> Janelas favoráveis</span>
          <strong>{insights.favorableDays} de {insights.nextSeven.length}</strong>
          <small>Vento máx. {insights.maxWind.toFixed(1)} m/s · Umidade mín. {insights.minHumidity.toFixed(0)}%</small>
        </article>
      </div>

      <div className="forecast-op-days" aria-label="Previsão operacional para sete dias">
        {insights.nextSeven.map(item => {
          const rain = Number(item.ppt || 0);
          const risks = evaluateForecastRisks(item);
          const attention = risks.length > 0;
          const favorable = isFavorableOperationalDay(item);
          return (
            <article key={item.date} className={attention ? 'needs-attention' : ''}>
              <div>
                <strong>{forecastDayLabel(item.date)}</strong>
                <small>{formatForecastDate(item.date)}</small>
              </div>
              <span className="forecast-op-day-icon">{weatherIcon(rain)}</span>
              <strong>{Number(item.tmax).toFixed(0)}° <small>{Number(item.tmin).toFixed(0)}°</small></strong>
              <span><FaCloudRain aria-hidden="true" /> {rain.toFixed(1)} mm</span>
              <em title={risks.length ? risks.map(risk => risk.label).join(', ') : favorable ? 'Atende aos critérios de chuva, vento, temperatura e umidade.' : 'Não atende a todos os critérios de janela operacional.'}>
                {attention
                  ? <><FaExclamationTriangle aria-hidden="true" /> Atenção</>
                  : favorable ? 'Boa janela' : 'Janela limitada'}
              </em>
            </article>
          );
        })}
      </div>
    </section>
  );
}
